import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CommandPalette, type PaletteItem, type PaletteSource } from '@beyond-js/ui/react';
import { usePresence } from '@accounts/session';
import { moved } from './screen';

/** The roles that administer an organization: its invitations, settings and activity are theirs */
const stewards = ['owner', 'admin'];

/**
 * ⌘K / Ctrl+K as the way to go anywhere in Accounts and do its common tasks (DQ-21): one palette per
 * page, with three sources asked as the person types: this interface's places, the person's
 * organizations (each one's team and, for its owners and administrators, its
 * invitations, settings and activity), and actions. Everything it lists is already in the person's own identity document, so
 * no source waits on the network. Recent items stay on this device. The sidebar's own ⌘K stays off.
 */
export function Palette() {
	const { text, identity, language } = usePresence();
	const navigate = useNavigate();

	const sources = useMemo<PaletteSource[]>(() => {
		const match = (query: string, items: PaletteItem[]) => {
			const wanted = query.trim().toLowerCase();
			return wanted ? items.filter(item => `${item.label} ${item.detail ?? ''}`.toLowerCase().includes(wanted)) : items;
		};
		const places: PaletteItem[] = [
			['overview', '/', 'nav.overview'],
			['organizations', '/organizations', 'nav.organizations'],
			['account', '/account', 'nav.account'],
			['security', '/security', 'nav.security'],
			['activity', '/activity', 'nav.activity'],
			['tokens', '/tokens', 'nav.tokens'],
			['connections', '/connections', 'nav.connections'],
			['notifications', '/notifications', 'nav.notifications'],
			['invitations', '/invitations', 'received.palette']
		].map(([id, href, key]) => ({ id: `place:${id}`, label: text(key), href }));
		const organizations: PaletteItem[] = (identity?.organizations ?? []).flatMap(entry => {
			const name = entry.name ?? entry.organization;
			const page = `/organizations/${encodeURIComponent(entry.organization)}`;
			const regions = ['members', ...(stewards.includes(entry.role) ? ['invitations', 'settings', 'audit'] : [])];
			return [
				{ id: `organization:${entry.organization}`, label: name, detail: text(`role.${entry.role}`), href: page },
				...regions.map(region => ({ id: `organization:${entry.organization}:${region}`, label: text(region === 'members' ? 'team.title' : `${region}.title`), detail: name, href: `${page}?view=${region}` }))
			];
		});
		const actions: PaletteItem[] = [
			{ id: 'action:create', label: text('organizations.create'), href: '/organizations?view=create' },
			{ id: 'action:factor', label: text('home.setup.factor.action'), href: '/security#factors' },
			{ id: 'action:token', label: text('tokens.new'), href: '/tokens/new' },
			{ id: 'action:leave', label: text('palette.signout'), href: '/leave' }
		];
		return [
			{ key: 'places', label: text('palette.places'), empty: true, search: query => match(query, places) },
			{ key: 'organizations', label: text('nav.organizations'), empty: true, search: query => match(query, organizations) },
			{ key: 'actions', label: text('palette.actions'), search: query => match(query, actions) }
		];
		// The language decides every label
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [identity?.organizations, language]);

	return (
		<CommandPalette
			sources={sources}
			// A new history excludes retired organization GitHub commands saved by earlier versions.
			recent={{ key: 'accounts:organization-pages' }}
			shortcut
			labels={CommandPalette.labels[language]}
			// ⌘/Ctrl+Enter opens a new tab in the palette itself; everything else is followed here
			onNavigate={item => {
				if (!item.href) return;
				const url = new URL(item.href, globalThis.location.origin);
				navigate(`${url.pathname}${url.search}${url.hash}`, { state: moved });
			}}
		/>
	);
}
